import { supabase } from '../lib/supabase';
import apiClient from './client';

export interface NotificationRule {
  id: string;
  name: string;
  description?: string;
  event_trigger: string;
  conditions?: any;
  template_id?: string | null;
  is_active: boolean;
  created_at?: string;
  recipients?: Array<{ id?: string; recipient_type: string; role_name?: string; specific_email?: string }>;
}

export interface Automation {
  id: string;
  name: string;
  trigger_event: string;
  conditions?: any;
  actions?: any;
  is_active: boolean;
  organization_id?: string;
  created_at?: string;
}

export const automationsApi = {
  // Notification Rules
  getNotificationRules: async (): Promise<NotificationRule[]> => {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('notification_rules')
          .select('*, recipients:notification_recipients(*)')
          .order('name');

        if (!error && data) return data as NotificationRule[];
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<NotificationRule[]>('/notification-rules');
      return data;
    } catch {
      return [];
    }
  },

  getNotificationRuleById: async (id: string): Promise<NotificationRule | null> => {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('notification_rules')
          .select('*, recipients:notification_recipients(*)')
          .eq('id', id)
          .maybeSingle();

        if (!error && data) return data as NotificationRule;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<NotificationRule>(`/notification-rules/${id}`);
      return data;
    } catch {
      return null;
    }
  },

  createNotificationRule: async (payload: Partial<NotificationRule>): Promise<NotificationRule> => {
    if (supabase) {
      const { recipients, ...ruleData } = payload;
      const { data, error } = await supabase
        .from('notification_rules')
        .insert(ruleData)
        .select()
        .single();

      if (error) throw error;

      if (recipients && recipients.length > 0 && data?.id) {
        const recipientRecords = recipients.map(r => ({
          rule_id: data.id,
          recipient_type: r.recipient_type,
          role_name: r.role_name || null,
          specific_email: r.specific_email || null
        }));
        await supabase.from('notification_recipients').insert(recipientRecords);
      }

      return data as NotificationRule;
    }

    const { data } = await apiClient.post<NotificationRule>('/notification-rules', payload);
    return data;
  },

  updateNotificationRule: async (id: string, updates: Partial<NotificationRule>): Promise<NotificationRule> => {
    if (supabase) {
      const { recipients, ...ruleData } = updates;
      const { data, error } = await supabase
        .from('notification_rules')
        .update(ruleData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as NotificationRule;
    }

    const { data } = await apiClient.put<NotificationRule>(`/notification-rules/${id}`, updates);
    return data;
  },

  deleteNotificationRule: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('notification_rules').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/notification-rules/${id}`);
    return data;
  },

  // Automations
  getAutomations: async (organizationId?: string): Promise<Automation[]> => {
    if (supabase) {
      try {
        let query = supabase.from('automations').select('*').order('name');
        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }
        const { data, error } = await query;
        if (!error && data) return data as Automation[];
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<Automation[]>('/automations', {
        params: { organizationId }
      });
      return data;
    } catch {
      return [];
    }
  },

  createAutomation: async (payload: Partial<Automation>): Promise<Automation> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('automations')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data as Automation;
    }

    const { data } = await apiClient.post<Automation>('/automations', payload);
    return data;
  },

  updateAutomation: async (id: string, updates: Partial<Automation>): Promise<Automation> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('automations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Automation;
    }

    const { data } = await apiClient.put<Automation>(`/automations/${id}`, updates);
    return data;
  },

  deleteAutomation: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('automations').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/automations/${id}`);
    return data;
  }
};
